/**
 * Duplicate checks for a lead, in the order the intake runs them (design: "Duplicate check
 * outcomes"):
 * 1. Looks like a support request → Help desk, not a lead.
 * 2. Same person (same email, or same mobile; office lines are shared, so never) → saved to
 *    that person's account without asking.
 * 3. Same company (same email domain, or same name in the same market) → ask before saving.
 * 4. Already a client → routed to the account manager.
 * 5. Outside our markets → the market waitlist.
 */
import type { Identifier } from "ra-core";

import type { Contact } from "../../types";
import type { ZcrmAccount } from "./accounts";
import { normalizeAccountName } from "./duplicates";
import {
  PUBLIC_EMAIL_DOMAINS,
  SUPPORT_REQUEST_WORDS,
  WAITLIST_MARKET,
} from "./leadConfig";
import type { ZcrmLead } from "./leads";

export type LeadMatchKind =
  | "support"
  | "same_person"
  | "same_company"
  | "existing_client"
  | "outside_markets"
  | "unfinished";

export interface LeadMatch {
  kind: LeadMatchKind;
  /** What the team does about it. */
  action: "help_desk" | "merge" | "ask" | "route" | "waitlist" | "reply";
  account?: ZcrmAccount;
  contactId?: Identifier;
  /** Why it matched, in a sentence. */
  reason: string;
}

type LeadFacts = Pick<
  ZcrmLead,
  "person" | "email" | "mobile" | "company" | "markets" | "request"
> & { company_id?: Identifier | null };

const lower = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

const digits = (value: string | null | undefined) =>
  (value ?? "").replace(/\D/g, "").slice(-10);

export const emailDomain = (email: string): string => {
  const domain = lower(email).split("@")[1] ?? "";
  return (PUBLIC_EMAIL_DOMAINS as readonly string[]).includes(domain)
    ? ""
    : domain;
};

const contactEmails = (contact: Contact) =>
  (contact.email_jsonb ?? []).map((e) => lower(e.email));

/** Mobile numbers only: the office line is shared by a whole company. */
const contactMobiles = (contact: Contact) =>
  (contact.phone_jsonb ?? [])
    .filter((p) => p.type !== "Work")
    .map((p) => digits(p.number));

const isClient = (account: ZcrmAccount) => account.view === "clients";

const looksLikeSupport = (request: string) =>
  (SUPPORT_REQUEST_WORDS as readonly string[]).some((word) =>
    lower(request).includes(word),
  );

/** Every check that applies to a lead, most important first. */
export const findLeadMatches = (
  lead: LeadFacts,
  accounts: ZcrmAccount[],
  contacts: Contact[],
  servedMarkets: string[],
): LeadMatch[] => {
  const matches: LeadMatch[] = [];
  const byId = new Map(accounts.map((a) => [String(a.id), a]));

  if (looksLikeSupport(lead.request)) {
    matches.push({
      kind: "support",
      action: "help_desk",
      reason:
        "Reads like a resident asking for app help. Move it to Help desk; it does not count as a lead.",
    });
  }

  const email = lower(lead.email);
  const mobile = digits(lead.mobile);
  const person = contacts.find(
    (c) =>
      (email && contactEmails(c).includes(email)) ||
      (mobile.length === 10 && contactMobiles(c).includes(mobile)),
  );
  const personAccount = person?.company_id
    ? byId.get(String(person.company_id))
    : undefined;
  if (person) {
    matches.push({
      kind: "same_person",
      action: "merge",
      account: personAccount,
      contactId: person.id,
      reason: `${person.first_name} ${person.last_name} is already a contact${
        personAccount ? ` at ${personAccount.name}` : ""
      } (same ${email && contactEmails(person).includes(email) ? "email" : "mobile"}). The lead joins them; nothing is duplicated.`,
    });
  }

  const domain = emailDomain(lead.email);
  const name = normalizeAccountName(lead.company);
  const companyMatch = personAccount
    ? undefined
    : accounts.find((account) => {
        const sameDomain =
          !!domain &&
          contacts.some(
            (c) =>
              String(c.company_id) === String(account.id) &&
              contactEmails(c).some((e) => e.endsWith(`@${domain}`)),
          );
        const sameName =
          !!name &&
          normalizeAccountName(account.name) === name &&
          (lead.markets.length === 0 ||
            !account.market ||
            lead.markets.includes(account.market));
        return sameDomain || sameName;
      });
  if (companyMatch) {
    matches.push({
      kind: "same_company",
      action: "ask",
      account: companyMatch,
      reason: `Same ${
        domain &&
        contacts.some(
          (c) =>
            String(c.company_id) === String(companyMatch.id) &&
            contactEmails(c).some((e) => e.endsWith(`@${domain}`)),
        )
          ? `email domain (${domain})`
          : "company name and market"
      } as ${companyMatch.name}. Confirm it is the same company before saving.`,
    });
  }

  const linked =
    (lead.company_id ? byId.get(String(lead.company_id)) : undefined) ??
    personAccount ??
    companyMatch;
  if (linked && isClient(linked)) {
    matches.push({
      kind: "existing_client",
      action: "route",
      account: linked,
      reason: `${linked.name} is already a client. The lead joins its account and its account manager follows up; no new account is created.`,
    });
  }

  const outside = lead.markets.filter(
    (m) => m === WAITLIST_MARKET || !servedMarkets.includes(m),
  );
  if (lead.markets.length > 0 && outside.length === lead.markets.length) {
    matches.push({
      kind: "outside_markets",
      action: "waitlist",
      reason:
        "Not a market we serve yet. Save it to the market waitlist instead of the pipeline.",
    });
  }

  if (!lead.person && !lead.mobile && !lead.company) {
    matches.push({
      kind: "unfinished",
      action: "reply",
      reason:
        "The visitor left before the chat finished. Reply by email while it is fresh.",
    });
  }
  return matches;
};

export const MATCH_LABELS: Record<LeadMatchKind, string> = {
  support: "Looks like a support request",
  same_person: "Same person",
  same_company: "Possible duplicate · ask",
  existing_client: "Existing client",
  outside_markets: "Outside our markets",
  unfinished: "Unfinished chat",
};

export const MATCH_TONES: Record<
  LeadMatchKind,
  "info" | "warning" | "success" | "neutral"
> = {
  support: "info",
  same_person: "success",
  same_company: "warning",
  existing_client: "success",
  outside_markets: "neutral",
  unfinished: "warning",
};
